import throttle from 'lodash.throttle';
import { useCallback, useEffect } from 'react';
import {
    LOGIN_ROUTE_AUTH_EXPIRED,
    SSO_LOGIN_ROUTE_AUTH_EXPIRED,
} from '../routes';
import { trackUiEvent } from './analytics/ui-tracking';
import { useAppState } from '../state';
import {
    getLastActiveTime,
    setLastActiveTime,
} from './useTerminateInactiveUserSession.utils';
import { isAuthenticatedWithSSO } from './ssoUtils';
import logger from '@upheal/utils/logger';

const watchedEvents = [
    'mousemove',
    'mousedown',
    'resize',
    'keydown',
    'touchstart',
    'wheel',
];
const twoHours = 2 * 60 * 60 * 1000;

type Props = {
    condition?: boolean;
    threshold?: number;
};

export const useTerminateInactiveUserSession = ({
    condition = true,
    threshold = twoHours,
}: Props) => {
    const { logout } = useAppState();

    const logoutIfInactive = useCallback(() => {
        const lastActiveTime = getLastActiveTime();
        const now = Date.now();
        if (lastActiveTime && now - lastActiveTime > threshold) {
            logger.debug(
                '[inactive-session-termination] logging out due to inactivity',
                { lastActiveTime, now, threshold },
            );

            trackUiEvent(
                'System',
                'Logout',
                undefined,
                'useTerminateInactiveUserSession',
            );
            const loginRoute = isAuthenticatedWithSSO()
                ? SSO_LOGIN_ROUTE_AUTH_EXPIRED
                : LOGIN_ROUTE_AUTH_EXPIRED;
            logout({ suppressWarning: true }).then(
                () => {
                    window.location.href = loginRoute;
                },
                () => {
                    // swallow
                },
            );
        }
    }, [threshold, logout]);

    useEffect(() => {
        if (!condition) {
            return;
        }

        // Check for inactivity BEFORE resetting the timer
        // This handles cases where the component was unmounted/remounted
        logoutIfInactive();

        setLastActiveTime();

        let mounted = true;
        const onEvent = throttle(
            () => {
                if (!mounted) {
                    return;
                }

                logoutIfInactive();
                setLastActiveTime();
            },
            5_000,
            {
                leading: true,
                trailing: true,
            },
        );

        const onVisibility = () => {
            if (!document.hidden) {
                onEvent();
                onEvent.flush();
            }
        };

        const onMessage = (event: MessageEvent) => {
            if (event.data.type === 'EXTENSION_RECORDING_IN_PROGRESS') {
                logger.debug(
                    '[inactive-session-termination] received recording activity from extension',
                );
                onEvent();
            }
        };

        logger.debug(
            '[inactive-session-termination] adding session inactivity listeners',
        );

        for (let i = 0; i < watchedEvents.length; i++) {
            window.addEventListener(watchedEvents[i], onEvent);
        }
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('message', onMessage);

        return () => {
            mounted = false;
            logger.debug(
                '[inactive-session-termination] removing session inactivity listeners',
            );

            for (let i = 0; i < watchedEvents.length; i++) {
                window.removeEventListener(watchedEvents[i], onEvent);
            }
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('message', onMessage);
        };
    }, [condition, logoutIfInactive]);
};
